import { useMemo, useState } from 'react'
import { layoutFan, fanNameFits, ahnenTerm, FAN_MAX_DEPTH, type FanSlot } from '../lib/fan'
import { maskName } from '../lib/security'
import type { Person, Relationship } from '../lib/types'

/**
 * Fan chart view (v0.24.0, D-143). The geometry lives in lib/fan.ts; this file
 * only decides how a wedge should look and what tapping one does.
 *
 * Two behaviours are worth knowing about:
 *  · A gap is a button, not a decoration. Tapping "her mother" opens the add-person
 *    form already wired to that relationship, so the chart doubles as the to-do list
 *    for the archive — the empty wedges ARE the remaining work.
 *  · Names are truncated to what the wedge can physically hold (fanNameFits), so the
 *    outer rings degrade to initials instead of overflowing into each other.
 */

const SIDE_COLOR = { father: '#60a5fa', mother: '#f472b6', root: '#e8bc6a' } as const

const shortYears = (p: Person) => {
  const b = p.birth_date?.slice(0, 4)
  const d = p.death_date?.slice(0, 4)
  if (b && d) return `${b}–${d}`
  if (b) return p.is_living ? b : `b. ${b}`
  return d ? `d. ${d}` : ''
}

export default function FanChart({
  persons, relationships, rootId, selected, veil, canEdit, onSelect, onAddParent,
}: {
  persons: Person[]
  relationships: Relationship[]
  rootId?: string
  selected: Person | null
  veil: boolean
  canEdit: boolean
  onSelect: (p: Person) => void
  onAddParent: (child: Person, side: 'father' | 'mother') => void
}) {
  const [depth, setDepth] = useState(() => Number(localStorage.getItem('fa_fan_depth')) || 4)
  const layout = useMemo(
    () => layoutFan(persons, relationships, rootId, { depth }),
    [persons, relationships, rootId, depth]
  )

  // "four generations" should mean four generations of people — a ring made up
  // entirely of gaps is drawn, but it is not something the family has recorded
  const gens = layout.slots.reduce((m, s) => (s.person ? Math.max(m, s.gen) : m), 0) + 1

  if (!rootId || layout.slots.length === 0) {
    return (
      <div className="rounded-2xl border border-white/[0.07] bg-white/[0.02] p-8 text-center">
        <p className="text-sm text-slate-400">A fan chart is drawn outward from one person.</p>
        <p className="text-xs text-slate-600 mt-1">Tap someone in the constellation first — or mark yourself in the tree and it will start from you.</p>
      </div>
    )
  }

  const label = (s: FanSlot) => {
    const p = s.person!
    const room = fanNameFits(s)
    const name = maskName(p, veil)
    if (room < 3) return (p.first_name[0] || '?').toUpperCase()
    return name.length > room ? `${name.slice(0, Math.max(1, room - 1))}…` : name
  }

  return (
    <div>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 mb-2 text-[11px] text-slate-500">
        <span><b className="text-slate-300">{layout.known}</b> people across {gens} generation{gens === 1 ? '' : 's'}</span>
        <span className="text-slate-600">direct ancestors only{persons.length > layout.known ? ` · ${persons.length - layout.known} more in the constellation` : ''}</span>
        {layout.gaps > 0 && (
          <span className="text-amber-300/80">
            {layout.gaps} {layout.gaps === 1 ? 'name is' : 'names are'} missing{canEdit ? ' — tap a dotted wedge to fill it in' : ''}
          </span>
        )}
        <span className="ml-auto flex items-center gap-1">
          <span className="text-slate-600">depth</span>
          {[3, 4, 5, FAN_MAX_DEPTH].map(d => (
            <button key={d} onClick={() => { setDepth(d); localStorage.setItem('fa_fan_depth', String(d)) }}
              className={`w-6 h-6 rounded-md text-[11px] ${depth === d ? 'bg-gold-500/20 text-gold-300' : 'text-slate-500 hover:text-slate-300 hover:bg-white/5'}`}>
              {d}
            </button>
          ))}
        </span>
      </div>

      <svg
        viewBox={`0 0 ${layout.width} ${layout.height}`}
        className="w-full rounded-2xl border border-white/[0.07]"
        style={{
          maxHeight: 'calc(100vh - 220px)',
          background: 'radial-gradient(circle at 50% 50%, #171130 0%, #0d0a1c 60%, #070510 100%)',
        }}
        role="img"
        aria-label={`Fan chart centred on ${layout.slots[0]?.person ? maskName(layout.slots[0].person, veil) : 'this person'}: ${layout.known} people across ${gens} generations, ${layout.gaps} names still missing`}
      >
        {layout.slots.filter(s => s.gen > 0).map(s => {
          const isSel = s.person && selected?.id === s.person.id
          const colour = SIDE_COLOR[s.side]
          const fade = 0.05 + Math.max(0, 0.13 - s.gen * 0.02)
          const clickable = s.person ? true : canEdit && !!s.childPerson
          return (
            <g key={s.ahnen}
              onClick={() => {
                if (s.person) onSelect(s.person)
                else if (canEdit && s.childPerson) onAddParent(s.childPerson, s.side === 'mother' ? 'mother' : 'father')
              }}
              style={{ cursor: clickable ? 'pointer' : 'default' }}>
              <title>
                {s.person
                  ? `${maskName(s.person, veil)}${shortYears(s.person) ? `, ${shortYears(s.person)}` : ''}`
                  : `Unrecorded ${s.side === 'mother' ? 'mother' : 'father'} of ${s.childPerson ? maskName(s.childPerson, veil) : 'someone'}`}
              </title>
              <path d={s.path}
                fill={s.person ? colour : '#ffffff'}
                fillOpacity={s.person ? (isSel ? 0.3 : fade) : 0.02}
                stroke={isSel ? '#e8bc6a' : s.person ? colour : '#8d86a8'}
                strokeOpacity={isSel ? 1 : s.person ? 0.4 : 0.3}
                strokeWidth={isSel ? 2 : 1}
                strokeDasharray={s.person ? undefined : '4 4'} />
              {s.person ? (
                <g transform={`translate(${s.labelX},${s.labelY}) rotate(${s.labelRotate})`}>
                  <text textAnchor="middle" y={s.spanDeg > 30 ? -2 : 4}
                    fontSize={s.gen <= 1 ? 12.5 : s.gen <= 3 ? 11 : 9.5}
                    fontWeight="600" fill="#e8e4f0">
                    {label(s)}
                  </text>
                  {s.spanDeg > 30 && shortYears(s.person) && (
                    <text textAnchor="middle" y="12" fontSize="9.5" fill="#8d86a8">{shortYears(s.person)}</text>
                  )}
                </g>
              ) : canEdit && s.childPerson ? (
                <g transform={`translate(${s.labelX},${s.labelY}) rotate(${s.labelRotate})`}>
                  <text textAnchor="middle" y="4" fontSize={s.gen <= 3 ? 13 : 10} fill="#8d86a8">+</text>
                </g>
              ) : null}
            </g>
          )
        })}

        {/* the root sits in the middle, drawn last so its ring is never overdrawn */}
        {layout.slots.filter(s => s.gen === 0).map(s => (
          <g key={s.ahnen} onClick={() => s.person && onSelect(s.person)} style={{ cursor: 'pointer' }}>
            <circle cx={layout.cx} cy={layout.cy} r={s.r1}
              fill="#1a1330" stroke="#e8bc6a" strokeOpacity={selected?.id === s.person?.id ? 1 : 0.55} strokeWidth="2" />
            <text x={layout.cx} y={layout.cy - 2} textAnchor="middle" fontSize="13" fontWeight="700" fill="#f0e6d2">
              {s.person ? maskName(s.person, veil).slice(0, 14) : ''}
            </text>
            <text x={layout.cx} y={layout.cy + 14} textAnchor="middle" fontSize="9.5" fill="#8d86a8">
              {s.person ? shortYears(s.person) : ''}
            </text>
          </g>
        ))}
      </svg>

      {/* The same chart as a list. A radial diagram is the one view a screen reader
          cannot narrate usefully, and the list is also how you read the pedigree
          aloud to a relative on the phone. */}
      <details className="mt-2">
        <summary className="text-[11px] text-slate-500 cursor-pointer hover:text-slate-300">Read this chart as a list</summary>
        <ol className="mt-2 space-y-0.5 text-xs">
          {layout.slots.map(s => (
            <li key={s.ahnen} className="flex gap-2">
              <span className="text-slate-600 w-40 flex-shrink-0">{ahnenTerm(s.ahnen)}</span>
              {s.person ? (
                <button onClick={() => onSelect(s.person!)} className="text-slate-300 hover:text-gold-300 text-left">
                  {maskName(s.person, veil)}{shortYears(s.person) ? ` · ${shortYears(s.person)}` : ''}
                </button>
              ) : (
                <span className="text-slate-600 italic">not yet recorded</span>
              )}
            </li>
          ))}
        </ol>
      </details>
    </div>
  )
}
